/**
 * Follower-goal design-store catalog. Mirrors `progressBarDesignsCatalog`:
 * a static gallery of self-contained card SVGs the user can apply to the
 * FollowerGoal widget. Recreated 1:1 from the Claude Design
 * `Follower Goal Widgets.html` handoff (10 variants), cropped from the original
 * 1920×1080 scenes (the `translate(120 760)` card group) to a tight 700×260
 * card and re-parametrised so the live follower count drives BOTH the readout
 * text (`current/target · pct%`) AND the fill geometry (bar width + the moving
 * head marker: pixel heart / cottage flower / anime heart).
 *
 * PURE-DATA format: each design is now a {@link SerializableDesign} (no JS
 * `build()`), so it can be stored / transmitted / PUBLISHED safely. The generic
 * {@link renderDesign} turns the data + the live `{current,target,pct}` values
 * into the final SVG: single values come from `slots`, the %-driven geometry
 * (fill widths, gloss/flower/heart markers, the pixel-block grid) from
 * `computed` formulas + `fragments` (showIf / fixed-index blocks). The bespoke
 * geometry the old `build()` computed in JS (gloss cx/rx, flower/heart offsets,
 * the 34px-pitch pixel blocks) is ported VERBATIM as formulas — see each
 * design's `computed` map.
 *
 * `renderFollowerGoalDesignSvg` is the single entry point the Store thumbnail,
 * the config picker/preview and the live Pixi renderer all call; its signature
 * is unchanged.
 *
 * Card-SVG conventions (shared with the other catalogs):
 *  - viewBox is `0 0 cardWidth cardHeight`; NO width/height attrs (the raster
 *    injects them).
 *  - every `<defs>`/gradient/filter/pattern/`<g id>`/clipPath id is prefixed
 *    with the design id (e.g. `dark-fgd-bar`) so 10 cards inline in one DOM
 *    document without id collisions; every `url(#…)`/`href="#…"` ref matches.
 *
 * Known caveat: the Cyberpunk (`フォロワー`), Anime VHS (`フォロワー`/`よろしく`)
 * and assorted emoji (♥ 🌱) keep the handoff's accents, but the display fonts
 * (Orbitron / VT323 / Audiowide …) carry no CJK/emoji glyphs, so those fall
 * back to a system face in the raster — intentional, matches the mock.
 */

import { renderDesign } from "../entities/designs/renderDesign";
import type { SerializableDesign } from "../entities/designs/serializableDesign";
import { getInstalledDesign } from "../entities/designs/installedRegistry";

export type FollowerGoalDesignCategory = "follower";

/** The numbers shown on the card. `pct` is already clamped to [0,100]. */
export interface FollowerGoalParts {
  /** Raw current count (may exceed `target`; the readout shows it verbatim). */
  current: number;
  target: number;
  /** `round(current/target*100)` clamped to [0,100] — drives fill + readout. */
  pct: number;
}

/**
 * A follower-goal design is just a {@link SerializableDesign} pinned to the
 * `followergoal` widget. The catalog-specific metadata (id/name/swatches/…) is
 * carried by the shared shape; the `category` is narrowed for this store.
 */
export interface FollowerGoalDesign extends SerializableDesign {
  widgetType: "followergoal";
  category: FollowerGoalDesignCategory;
}

const clamp = (n: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, n));

// ── 01 · Dark Minimal ──────────────────────────────────────────────────────
// fillW(pct, 604); fill rect shown when w > 0.
const darkDesign: FollowerGoalDesign = {
  id: "dark",
  name: "Dark Minimal",
  subtitle: "Reference style",
  widgetType: "followergoal",
  category: "follower",
  description:
    '"FOLLOWER GOAL" left, "current/target · pct%" right, soft violet rounded bar and a caption below.',
  swatches: ["#0d0d12", "#1f1f29", "#a07dff", "#f5f5f5"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Inter", "DM Mono"],
  slots: {
    current: { kind: "number", format: "plain" },
    target: { kind: "number", format: "plain" },
    pct: { kind: "number", format: "plain" },
  },
  computed: { w: "round(604 * pct / 100)" },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="dark-fgd-dots" width="22" height="22" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1.1" fill="#33333d" opacity=".6"/></pattern>
    <linearGradient id="dark-fgd-bar" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#7d5cff"/><stop offset="100%" stop-color="#a07dff"/></linearGradient>
  </defs>
  <rect width="700" height="260" rx="22" fill="#0d0d12" stroke="#2a2a33" stroke-width="1.5"/>
  <rect width="700" height="260" rx="22" fill="url(#dark-fgd-dots)"/>
  <text x="48" y="92" font-family="Inter, sans-serif" font-weight="700" font-size="28" fill="#f5f5f5" letter-spacing="3">FOLLOWER GOAL</text>
  <text x="652" y="92" text-anchor="end" font-family="DM Mono, monospace" font-weight="500" font-size="26" fill="#cfd6e2">{{current}}/{{target}} · {{pct}}%</text>
  <rect x="48" y="120" width="604" height="34" rx="17" fill="#1f1f29"/>
  {{FRAGMENTS}}
  <text x="48" y="208" font-family="Inter, sans-serif" font-weight="500" font-size="20" fill="#8a8f9c">¡Ayúdanos a llegar a la meta! ♥</text>
</svg>`,
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="48" y="120" width="{{w}}" height="34" rx="17" fill="url(#dark-fgd-bar)"/>`,
    },
  ],
};

// ── 02 · Risograph ─────────────────────────────────────────────────────────
// fillW(pct, 624); two offset fill rects shown when w > 0.
const twitchDesign: FollowerGoalDesign = {
  id: "twitch",
  name: "Risograph",
  subtitle: "Two-color offset",
  widgetType: "followergoal",
  category: "follower",
  description:
    "Misregistered red/blue riso card with halftone dots, an offset-printed goal readout and a hand-stamped serial.",
  swatches: ["#ede4cf", "#e84a4a", "#2b4cbf", "#222018"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Anton", "DM Mono"],
  slots: {
    current: { kind: "number", format: "plain" },
    target: { kind: "number", format: "plain" },
    pct: { kind: "number", format: "plain" },
  },
  computed: { w: "round(624 * pct / 100)" },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="twitch-fgd-paper" width="3" height="3" patternUnits="userSpaceOnUse"><rect width="3" height="3" fill="#ede4cf"/><circle cx="1" cy="2" r="0.4" fill="#222018" opacity=".18"/></pattern>
    <pattern id="twitch-fgd-dots" width="8" height="8" patternUnits="userSpaceOnUse"><circle cx="4" cy="4" r="2.4" fill="#e84a4a"/></pattern>
  </defs>
  <rect width="700" height="260" fill="url(#twitch-fgd-paper)" stroke="#222018" stroke-width="3"/>
  <rect x="8" y="8" width="684" height="244" fill="none" stroke="#222018" stroke-width="1" stroke-dasharray="8 6"/>
  <circle cx="636" cy="66" r="42" fill="url(#twitch-fgd-dots)" opacity=".75"/>
  <circle cx="644" cy="72" r="42" fill="#2b4cbf" opacity=".45" style="mix-blend-mode:multiply"/>
  <text x="36" y="86" font-family="Anton, sans-serif" font-size="38" fill="#2b4cbf" opacity=".88" letter-spacing="1">FOLLOWER GOAL</text>
  <text x="33" y="88" font-family="Anton, sans-serif" font-size="38" fill="#e84a4a" opacity=".88" letter-spacing="1">FOLLOWER GOAL</text>
  <text x="520" y="116" text-anchor="end" font-family="DM Mono, monospace" font-size="20" fill="#222018" letter-spacing="4">{{current}} / {{target}} · {{pct}}%</text>
  <rect x="36" y="140" width="628" height="34" fill="none" stroke="#2b4cbf" stroke-width="4" transform="translate(5 4)"/>
  <rect x="36" y="140" width="628" height="34" fill="none" stroke="#e84a4a" stroke-width="4"/>
  {{FRAGMENTS}}
  <text x="36" y="216" font-family="DM Mono, monospace" font-size="14" fill="#222018" letter-spacing="6">¡AYÚDANOS A LLEGAR A LA META! · N°042</text>
  <g transform="translate(600 214) rotate(-8)"><rect x="-58" y="-22" width="116" height="40" fill="none" stroke="#e84a4a" stroke-width="2"/><text x="0" y="5" text-anchor="middle" font-family="DM Mono, monospace" font-size="13" fill="#e84a4a" letter-spacing="2">SEGUINOS</text></g>
</svg>`,
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="40" y="144" width="{{w}}" height="26" fill="#e84a4a" opacity=".85"/>`,
    },
    {
      showIf: "w > 0",
      svg: `<rect x="43" y="147" width="{{w}}" height="26" fill="#2b4cbf" opacity=".5" style="mix-blend-mode:multiply"/>`,
    },
  ],
};

// ── 03 · Y2K Aqua Gel ──────────────────────────────────────────────────────
// fillW(pct, 598); glossCx = round(54 + w/2); glossRx = max(0, round(w/2 - 12)).
// Fill rect shown when w > 0; gloss ellipse shown when glossRx > 0.
const y2kDesign: FollowerGoalDesign = {
  id: "y2k",
  name: "Y2K Aqua Gel",
  subtitle: "Glossy",
  widgetType: "followergoal",
  category: "follower",
  description:
    "Aqua gel capsule with chrome gloss, green liquid fill and the goal readout in Audiowide.",
  swatches: ["#003a7a", "#1c8fff", "#8acfff", "#5aff8a"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Audiowide", "DM Mono"],
  slots: {
    current: { kind: "number", format: "plain" },
    target: { kind: "number", format: "plain" },
    pct: { kind: "number", format: "plain" },
  },
  computed: {
    w: "round(598 * pct / 100)",
    glossCx: "round(54 + w / 2)",
    glossRx: "max(0, round(w / 2 - 12))",
  },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="y2k-fgd-bg" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#cfeaff"/><stop offset="55%" stop-color="#1c8fff"/><stop offset="100%" stop-color="#003a7a"/></linearGradient>
    <linearGradient id="y2k-fgd-fill" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#caffd6"/><stop offset="45%" stop-color="#5aff8a"/><stop offset="100%" stop-color="#2cd86a"/></linearGradient>
  </defs>
  <rect width="700" height="260" rx="40" fill="url(#y2k-fgd-bg)" stroke="#fff" stroke-width="3"/>
  <ellipse cx="350" cy="30" rx="300" ry="12" fill="#fff" opacity=".7"/>
  <g transform="translate(40 40)"><circle r="10" fill="#003a7a"/><circle r="6" fill="#5aff8a"/></g>
  <text x="62" y="48" font-family="Audiowide, sans-serif" font-size="26" fill="#fff" letter-spacing="2">FOLLOWER GOAL</text>
  <text x="652" y="48" text-anchor="end" font-family="Audiowide, sans-serif" font-size="24" fill="#5aff8a">{{current}}/{{target}}·{{pct}}%</text>
  <rect x="48" y="110" width="604" height="50" rx="25" fill="rgba(0,30,80,.5)" stroke="#fff" stroke-width="2"/>
  {{FRAGMENTS}}
  <text x="48" y="216" font-family="DM Mono, monospace" font-size="15" fill="#cffff4" letter-spacing="6">¡AYÚDANOS A LLEGAR A LA META! ♥</text>
</svg>`,
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="54" y="116" width="{{w}}" height="38" rx="19" fill="url(#y2k-fgd-fill)"/>`,
    },
    {
      showIf: "glossRx > 0",
      svg: `<ellipse cx="{{glossCx}}" cy="128" rx="{{glossRx}}" ry="6" fill="#fff" opacity=".6"/>`,
    },
  ],
};

// ── 04 · Cyberpunk Neon ────────────────────────────────────────────────────
// fillW(pct, 628); notched neon fill rect shown when w > 0 (inside the bar group).
const cyberDesign: FollowerGoalDesign = {
  id: "cyber",
  name: "Cyberpunk Neon",
  subtitle: "HUD node",
  widgetType: "followergoal",
  category: "follower",
  description:
    'Chamfered HUD with "FOLLOWER.NODE" tag, notched neon fill and katakana accent.',
  swatches: ["#080010", "#00f0ff", "#ff2e88", "#f5d019"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["DM Mono", "Orbitron"],
  slots: {
    current: { kind: "number", format: "plain" },
    target: { kind: "number", format: "plain" },
    pct: { kind: "number", format: "plain" },
  },
  computed: { w: "round(628 * pct / 100)" },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs><filter id="cyber-fgd-g"><feGaussianBlur stdDeviation="2.5"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
  <g filter="url(#cyber-fgd-g)">
    <polygon points="0,40 40,0 660,0 700,40 700,220 660,260 40,260 0,220" fill="#080010" stroke="#ff2e88" stroke-width="2.5"/>
    <polygon points="14,46 50,14 650,14 686,46 686,214 650,246 50,246 14,214" fill="none" stroke="#00f0ff" stroke-width="1.5" opacity=".7"/>
    <text x="34" y="64" font-family="DM Mono, monospace" font-size="20" fill="#00f0ff" letter-spacing="6">▮ FOLLOWER.NODE · フォロワー</text>
    <text x="666" y="64" text-anchor="end" font-family="Orbitron, sans-serif" font-weight="900" font-size="26" fill="#f5d019">{{current}}/{{target}}·{{pct}}%</text>
    <g transform="translate(34 110)">
      <rect width="632" height="40" fill="none" stroke="#00f0ff" stroke-width="1.5"/>
      {{FRAGMENTS}}
      <g fill="#080010"><rect x="64" y="4" width="3" height="32"/><rect x="128" y="4" width="3" height="32"/><rect x="192" y="4" width="3" height="32"/><rect x="256" y="4" width="3" height="32"/><rect x="320" y="4" width="3" height="32"/><rect x="384" y="4" width="3" height="32"/></g>
      <g stroke="#ff2e88" stroke-width="2"><line x1="316" y1="-8" x2="316" y2="48"/></g>
    </g>
    <text x="34" y="216" font-family="DM Mono, monospace" font-size="14" fill="#88e8d8" letter-spacing="6">// SEGUÍ EL CANAL · NEXT NODE @ 75%</text>
  </g>
</svg>`,
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="4" y="4" width="{{w}}" height="32" fill="#00f0ff"/>`,
    },
  ],
};

// ── 05 · Editorial ─────────────────────────────────────────────────────────
// fillW(pct, 632); ochre fill rect shown when w > 0.
const editorialDesign: FollowerGoalDesign = {
  id: "editorial",
  name: "Editorial",
  subtitle: "Subscription drive",
  widgetType: "followergoal",
  category: "follower",
  description:
    'Cream card with double rule, italic "Meta de seguidores" and a thin ochre fill with serif readout.',
  swatches: ["#f5efe2", "#1a1815", "#c8a050", "#8a3a2a"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Cormorant Garamond", "DM Mono"],
  slots: {
    current: { kind: "number", format: "plain" },
    target: { kind: "number", format: "plain" },
    pct: { kind: "number", format: "plain" },
  },
  computed: { w: "round(632 * pct / 100)" },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <rect width="700" height="260" fill="#f5efe2"/>
  <line x1="24" y1="22" x2="676" y2="22" stroke="#1a1815" stroke-width="3"/>
  <line x1="24" y1="30" x2="676" y2="30" stroke="#1a1815" stroke-width="1"/>
  <text x="32" y="86" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="44" fill="#1a1815">Meta de seguidores</text>
  <text x="668" y="86" text-anchor="end" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="38" fill="#8a3a2a">{{current}} / {{target}}</text>
  <rect x="32" y="116" width="636" height="22" fill="none" stroke="#1a1815" stroke-width="1.5"/>
  {{FRAGMENTS}}
  <line x1="32" y1="166" x2="668" y2="166" stroke="#1a1815" stroke-width="1"/>
  <text x="32" y="204" font-family="DM Mono, monospace" font-size="14" fill="#1a1815" letter-spacing="8">{{pct}}% · ¡AYÚDANOS A LLEGAR A LA META!</text>
</svg>`,
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="34" y="118" width="{{w}}" height="18" fill="#c8a050"/>`,
    },
  ],
};

// ── 06 · Pixel Arcade ──────────────────────────────────────────────────────
// The "FOLLOWER GOAL" tab overhangs the card top by 16px in the source, so the
// whole card is shifted down 16 into a 700×276 viewBox.
//
// fillW(pct, 606). The original tiled 28px blocks on a 34px pitch from x=6 up to
// the fill edge: `for (x=6; x<6+w; x+=34) { bw = min(28, 6+w-x); rect x w=bw }`.
// Ported as 18 FIXED-index block fragments (block j lives at x=6+34*j, visible
// when w > 34*j, width = min(28, w - 34*j)); the renderer has no per-iteration
// formula eval, so the widths are precomputed as `bwN` and the x is the literal
// constant. j∈[0,17] covers the full bar (ceil(606/34)=18 blocks max).
// Heart leads the fill edge: heartX = 58 + w, shown when w > 0.
const PIXEL_BLOCK_X = [
  6, 40, 74, 108, 142, 176, 210, 244, 278, 312, 346, 380, 414, 448, 482, 516,
  550, 584,
];
const pixelBlockComputed: Record<string, string> = {
  w: "round(606 * pct / 100)",
  heartX: "58 + w",
};
const pixelBlockFragments = PIXEL_BLOCK_X.map((x, j) => {
  pixelBlockComputed[`bw${j}`] = `min(28, w - ${34 * j})`;
  return {
    showIf: `w > ${34 * j}`,
    svg: `<rect x="${x}" y="6" width="{{bw${j}}}" height="28"/>`,
  };
});
const pixelDesign: FollowerGoalDesign = {
  id: "pixel",
  name: "Pixel Arcade",
  subtitle: "Party meter",
  widgetType: "followergoal",
  category: "follower",
  description:
    '8-bit dialog box with a blocky fill, heart sprite and "FOLLOWER GOAL" in Press Start 2P.',
  swatches: ["#1d2a3a", "#ffb703", "#48cae4", "#ff3d4d"],
  cardWidth: 700,
  cardHeight: 276,
  fonts: ["Press Start 2P"],
  slots: {
    current: { kind: "number", format: "plain" },
    target: { kind: "number", format: "plain" },
    pct: { kind: "number", format: "plain" },
  },
  computed: pixelBlockComputed,
  frame: `<svg viewBox="0 0 700 276" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">
  <g transform="translate(0 16)">
    <rect width="700" height="260" fill="#48cae4"/>
    <rect x="8" y="8" width="684" height="244" fill="#1d2a3a"/>
    <rect x="16" y="16" width="668" height="228" fill="#0a121c"/>
    <g fill="#ffb703"><rect width="24" height="24"/><rect x="676" width="24" height="24"/><rect y="236" width="24" height="24"/><rect x="676" y="236" width="24" height="24"/></g>
    <g transform="translate(40 -16)"><rect width="300" height="32" fill="#ff3d4d"/><rect x="6" y="6" width="288" height="20" fill="#1d2a3a"/><text x="150" y="22" text-anchor="middle" font-family="Press Start 2P, monospace" font-size="12" fill="#ffb703" letter-spacing="1">FOLLOWER GOAL</text></g>
    <g font-family="Press Start 2P, monospace" fill="#fff"><text x="44" y="98" font-size="16" fill="#48cae4">{{current}} / {{target}}</text><text x="656" y="98" text-anchor="end" font-size="16" fill="#ffb703">{{pct}}%</text></g>
    <g transform="translate(44 120)"><rect width="612" height="40" fill="#000" stroke="#48cae4" stroke-width="3"/><g fill="#ff3d4d">{{FRAGMENTS:blocks}}</g></g>
    {{FRAGMENTS:heart}}
    <text x="44" y="208" font-family="Press Start 2P, monospace" font-size="11" fill="#48cae4">FOLLOW TO FILL THE BAR!</text>
  </g>
</svg>`,
  fragments: [
    ...pixelBlockFragments.map((f) => ({ ...f, key: "blocks" })),
    {
      key: "heart",
      showIf: "w > 0",
      svg: `<g transform="translate({{heartX}} 140)" fill="#ff3d4d"><rect x="0" y="6" width="6" height="6"/><rect x="6" y="0" width="6" height="6"/><rect x="18" y="0" width="6" height="6"/><rect x="24" y="6" width="6" height="6"/><rect x="0" y="12" width="30" height="6"/><rect x="6" y="18" width="18" height="6"/><rect x="12" y="24" width="6" height="6"/></g>`,
    },
  ],
};

// ── 07 · Cottagecore ───────────────────────────────────────────────────────
// fillW(pct, 600); flowerX = 52 + w. Fill rect + flower shown when w > 0.
const cottageDesign: FollowerGoalDesign = {
  id: "cottage",
  name: "Cottagecore",
  subtitle: "Stitched vine",
  widgetType: "followergoal",
  category: "follower",
  description:
    "Linen card with cross-stitch border, italic goal and a vine fill with flower markers.",
  swatches: ["#f0e4cc", "#6b4a2a", "#b85a4a", "#5a7a4a"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Cormorant Garamond", "DM Mono"],
  slots: {
    current: { kind: "number", format: "plain" },
    target: { kind: "number", format: "plain" },
  },
  computed: { w: "round(600 * pct / 100)", flowerX: "52 + w" },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="cottage-fgd-linen" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="#f7eed5"/><rect width="1" height="4" fill="#d8c8a8" opacity=".55"/><rect y="2" width="4" height="1" fill="#d8c8a8" opacity=".4"/></pattern>
    <pattern id="cottage-fgd-st" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M5 5 L19 19 M19 5 L5 19" stroke="#b85a4a" stroke-width="2.5" stroke-linecap="round"/></pattern>
    <g id="cottage-fgd-flower"><circle r="12" fill="#ff9da8"/><circle cx="-12" cy="-5" r="9" fill="#ff9da8"/><circle cx="12" cy="-5" r="9" fill="#ff9da8"/><circle cx="-7" cy="9" r="9" fill="#ff9da8"/><circle cx="7" cy="9" r="9" fill="#ff9da8"/><circle r="5" fill="#ffd060"/></g>
  </defs>
  <rect width="700" height="260" fill="url(#cottage-fgd-linen)" stroke="#6b4a2a" stroke-width="2"/>
  <rect width="700" height="24" fill="url(#cottage-fgd-st)"/>
  <rect y="236" width="700" height="24" fill="url(#cottage-fgd-st)"/>
  <rect width="24" height="260" fill="url(#cottage-fgd-st)"/>
  <rect x="676" width="24" height="260" fill="url(#cottage-fgd-st)"/>
  <text x="48" y="92" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="40" fill="#2a1a08">Meta de seguidores</text>
  <text x="652" y="92" text-anchor="end" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="36" fill="#5a7a4a">{{current}}/{{target}}</text>
  <rect x="48" y="120" width="604" height="26" rx="13" fill="#e8dcc0" stroke="#6b4a2a" stroke-width="1.5"/>
  {{FRAGMENTS:fill}}
  <path d="M52 133 q40 -10 80 0 q40 10 80 0 q40 -10 80 0 q40 10 80 0" stroke="#7a9a5a" stroke-width="2" fill="none"/>
  {{FRAGMENTS:flower}}
  <text x="48" y="210" font-family="DM Mono, monospace" font-size="14" fill="#b85a4a" letter-spacing="6">¡ayúdanos a que el jardín florezca! 🌱</text>
</svg>`,
  fragments: [
    {
      key: "fill",
      showIf: "w > 0",
      svg: `<rect x="52" y="124" width="{{w}}" height="18" rx="9" fill="#5a7a4a"/>`,
    },
    {
      key: "flower",
      showIf: "w > 0",
      svg: `<use href="#cottage-fgd-flower" transform="translate({{flowerX}} 133) scale(0.8)"/>`,
    },
  ],
};

// ── 08 · Brutalist ─────────────────────────────────────────────────────────
// fillW(pct, 620); orange fill rect shown when w > 0.
const brutalistDesign: FollowerGoalDesign = {
  id: "brutalist",
  name: "Brutalist",
  subtitle: "Ledger",
  widgetType: "followergoal",
  category: "follower",
  description:
    "Black slab with orange bar, huge Anton count and a hard fill with mono readout.",
  swatches: ["#1a1a1a", "#f5f5f5", "#ff5a2a", "#c8c2b8"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Anton", "DM Mono"],
  slots: {
    current: { kind: "number", format: "plain" },
    target: { kind: "number", format: "plain" },
    pct: { kind: "number", format: "plain" },
  },
  computed: { w: "round(620 * pct / 100)" },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <rect width="700" height="260" fill="#1a1a1a"/>
  <rect width="24" height="260" fill="#ff5a2a"/>
  <text x="48" y="58" font-family="DM Mono, monospace" font-size="14" fill="#ff5a2a" letter-spacing="12">// FOLLOWER GOAL</text>
  <text x="48" y="150" font-family="Anton, sans-serif" font-size="100" fill="#f5f5f5" letter-spacing="-2">{{current}}<tspan font-size="48" fill="#c8c2b8">/{{target}}</tspan></text>
  <text x="668" y="100" text-anchor="end" font-family="Anton, sans-serif" font-size="48" fill="#ff5a2a">{{pct}}%</text>
  <rect x="48" y="178" width="620" height="30" fill="#0a0a0a" stroke="#c8c2b8" stroke-width="1"/>
  {{FRAGMENTS}}
  <text x="48" y="240" font-family="DM Mono, monospace" font-size="13" fill="#c8c2b8" letter-spacing="8">SEGUÍ EL CANAL · @ERIOL.STREAMS</text>
</svg>`,
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="48" y="178" width="{{w}}" height="30" fill="#ff5a2a"/>`,
    },
  ],
};

// ── 09 · Anime VHS ─────────────────────────────────────────────────────────
// Cyan tracking ghost rect (x=6 w=700, stroke 3) reaches x≈707.5; card is 708
// wide so its right stroke isn't clipped. Whole card wrapped in the RGB filter.
//
// fillW(pct, 626). Heart marker authored at the fill edge x=462 (=36+426);
// shift = clamp(36 + w - 462, -426, 194). Fill rect + heart shown when w > 0.
const animeDesign: FollowerGoalDesign = {
  id: "anime",
  name: "Anime VHS",
  subtitle: "フォロワー",
  widgetType: "followergoal",
  category: "follower",
  description:
    "Pink double-stroke card with cyan tracking ghost, katakana corner and a heart-tipped RGB-split fill.",
  swatches: ["#0a0218", "#ff7ad0", "#7ad0ff", "#fff7c2"],
  cardWidth: 708,
  cardHeight: 260,
  fonts: ["VT323", "DM Mono"],
  slots: {
    current: { kind: "number", format: "plain" },
    target: { kind: "number", format: "plain" },
    pct: { kind: "number", format: "plain" },
  },
  computed: {
    w: "round(626 * pct / 100)",
    shift: "clamp(36 + w - 462, -426, 194)",
  },
  frame: `<svg viewBox="0 0 708 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="anime-fgd-rgb"><feOffset in="SourceGraphic" dx="-3" dy="0" result="r"/><feColorMatrix in="r" values="1 0 0 0 1   0 0 0 0 0.5   0 0 0 0 0.8   0 0 0 1 0" result="rr"/><feOffset in="SourceGraphic" dx="3" dy="0" result="c"/><feColorMatrix in="c" values="0 0 0 0 0.5   0 0 0 0 0.9   0 0 0 0 1   0 0 0 1 0" result="cc"/><feMerge><feMergeNode in="rr"/><feMergeNode in="cc"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <pattern id="anime-fgd-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#ff7ad0" opacity=".15"/></pattern>
    <linearGradient id="anime-fgd-fill" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#ff7ad0"/><stop offset="100%" stop-color="#7ad0ff"/></linearGradient>
  </defs>
  <g filter="url(#anime-fgd-rgb)">
    <rect x="6" y="0" width="700" height="260" fill="none" stroke="#7ad0ff" stroke-width="3" opacity=".55"/>
    <rect width="700" height="260" fill="#0a0218" stroke="#ff7ad0" stroke-width="4"/>
    <rect x="10" y="10" width="680" height="240" fill="none" stroke="#7ad0ff" stroke-width="1.5" opacity=".55"/>
    <rect width="700" height="260" fill="url(#anime-fgd-scan)"/>
    <g><rect width="260" height="36" fill="#ff7ad0"/><text x="14" y="26" font-family="DM Mono, monospace" font-size="16" fill="#0a0218" letter-spacing="4">フォロワー · FOLLOWER</text></g>
    <text x="32" y="116" font-family="VT323, monospace" font-size="40" fill="#fff7c2" letter-spacing="4">FOLLOWER GOAL</text>
    <text x="666" y="116" text-anchor="end" font-family="VT323, monospace" font-size="40" fill="#ff7ad0">{{current}}/{{target}}·{{pct}}%</text>
    <rect x="32" y="138" width="636" height="36" fill="#1a0a2a" stroke="#7ad0ff" stroke-width="2"/>
    {{FRAGMENTS}}
    <text x="32" y="216" font-family="VT323, monospace" font-size="24" fill="#7ad0ff" letter-spacing="6">▮ ¡SEGUÍ EL CANAL! · よろしく !</text>
  </g>
</svg>`,
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="36" y="142" width="{{w}}" height="28" fill="url(#anime-fgd-fill)"/>`,
    },
    {
      showIf: "w > 0",
      svg: `<g transform="translate({{shift}} 0)"><path d="M462 156 q-10 -10 0 -20 q10 -10 20 0 q10 -10 20 0 q10 10 0 20 l-20 18 z" fill="#fff7c2"/></g>`,
    },
  ],
};

// ── 10 · Synthwave Sunset ──────────────────────────────────────────────────
// fillW(pct, 623); chrome fill rect shown when w > 0.
const holoDesign: FollowerGoalDesign = {
  id: "holo",
  name: "Synthwave Sunset",
  subtitle: "Grid horizon",
  widgetType: "followergoal",
  category: "follower",
  description:
    "Sunset gradient card with a striped sun, magenta perspective grid baseline and a chrome goal readout.",
  swatches: ["#1a0635", "#ff3d8a", "#ffb347", "#2ce5ff"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Audiowide"],
  slots: {
    current: { kind: "number", format: "plain" },
    target: { kind: "number", format: "plain" },
    pct: { kind: "number", format: "plain" },
  },
  computed: { w: "round(623 * pct / 100)" },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="holo-fgd-sky" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#1a0635"/><stop offset="60%" stop-color="#7a1f6b"/><stop offset="100%" stop-color="#ff3d5a"/></linearGradient>
    <linearGradient id="holo-fgd-sun" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#fff1a8"/><stop offset="50%" stop-color="#ffb347"/><stop offset="100%" stop-color="#ff3d8a"/></linearGradient>
    <linearGradient id="holo-fgd-chrome" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#fff1a8"/><stop offset="50%" stop-color="#ffb347"/><stop offset="55%" stop-color="#ff7ad0"/><stop offset="100%" stop-color="#ff3d8a"/></linearGradient>
    <clipPath id="holo-fgd-clip"><rect width="700" height="260" rx="10"/></clipPath>
  </defs>
  <g clip-path="url(#holo-fgd-clip)">
    <rect width="700" height="260" fill="url(#holo-fgd-sky)"/>
    <g transform="translate(560 150)"><circle r="120" fill="url(#holo-fgd-sun)"/><g fill="url(#holo-fgd-sky)"><rect x="-120" y="30" width="240" height="6"/><rect x="-120" y="46" width="240" height="8"/><rect x="-120" y="68" width="240" height="12"/></g></g>
    <g stroke="#ff3d8a" stroke-width="1.5" opacity=".85"><line x1="0" y1="214" x2="700" y2="214"/><line x1="0" y1="234" x2="700" y2="234"/><line x1="0" y1="258" x2="700" y2="258"/><line x1="-60" y1="260" x2="180" y2="214"/><line x1="160" y1="260" x2="290" y2="214"/><line x1="350" y1="260" x2="350" y2="214"/><line x1="540" y1="260" x2="410" y2="214"/><line x1="760" y1="260" x2="520" y2="214"/></g>
    <g fill="#fff"><circle cx="60" cy="44" r="1.6"/><circle cx="160" cy="30" r="1.2"/><circle cx="260" cy="54" r="1.4"/></g>
  </g>
  <rect width="700" height="260" rx="10" fill="none" stroke="#fff" stroke-width="2" opacity=".8"/>
  <text x="36" y="70" font-family="Audiowide, sans-serif" font-size="26" fill="#fff1a8" letter-spacing="4">FOLLOWER GOAL</text>
  <text x="664" y="70" text-anchor="end" font-family="Audiowide, sans-serif" font-size="22" fill="#2ce5ff">{{current}}/{{target}}·{{pct}}%</text>
  <rect x="36" y="96" width="628" height="40" rx="20" fill="rgba(10,2,32,.55)" stroke="#fff" stroke-width="2"/>
  {{FRAGMENTS}}
  <text x="36" y="180" font-family="Audiowide, sans-serif" font-size="16" fill="#fff1a8" letter-spacing="6">¡AYÚDANOS A LLEGAR A LA META!</text>
</svg>`,
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="41" y="101" width="{{w}}" height="30" rx="15" fill="url(#holo-fgd-chrome)"/>`,
    },
  ],
};

export const FOLLOWER_GOAL_DESIGNS: FollowerGoalDesign[] = [
  darkDesign,
  twitchDesign,
  y2kDesign,
  cyberDesign,
  editorialDesign,
  pixelDesign,
  cottageDesign,
  brutalistDesign,
  animeDesign,
  holoDesign,
];

/**
 * Render a design's card SVG for the given counts. `current` is shown verbatim
 * (may exceed `target`); `pct = round(current/target*100)` is clamped to
 * [0,100] and drives the fill geometry + the readout percentage. Delegates to
 * the generic {@link renderDesign}; the bespoke geometry lives in the design's
 * `computed`/`fragments` data.
 */
export function renderFollowerGoalDesignSvg(
  design: FollowerGoalDesign,
  counts: { current: number; target: number },
): string {
  const target = Math.max(1, Math.round(counts.target));
  const current = Math.max(0, Math.round(counts.current));
  const pct = clamp(Math.round((current / target) * 100), 0, 100);
  return renderDesign(design, { values: { current, target, pct } });
}

export function getFollowerGoalDesign(
  id: string | undefined,
): FollowerGoalDesign | undefined {
  if (!id) {
    return undefined;
  }
  const local = FOLLOWER_GOAL_DESIGNS.find((d) => d.id === id);
  if (local) {
    return local;
  }
  // Not a first-party id — fall back to an INSTALLED community design (hydrated
  // into the registry by `useDesignLibrary`). The registry lookup is constrained
  // to the `followergoal` widgetType, so the resolved row is a follower-goal
  // SerializableDesign and the cast is safe for the renderer/config consumers.
  return getInstalledDesign(id, "followergoal") as
    | FollowerGoalDesign
    | undefined;
}
